import { useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import { orderBy, sortBy } from "lodash";
import { callApi } from "../../api";
// eslint-disable-next-line import/no-cycle
import { useAuth } from "../useAuth";
import { formatProductCategories, parseJSON } from "../../utility/CustomFunction";

export default () => {
  const [inventories, setInventories] = useState(null);
  const [vendors, setVendors] = useState(null);
  const [poConfig, setPoConfig] = useState("");
  const [isRefresh, setRefresh] = useState(false);
  const [inventoryLoading, setInventoryLoading] = useState(false);
  const [selectedVendorItem, setSelectedVendorItems] = useState({
    count: 0,
    rows: [],
  });
  const [multipleLocationProducts, setMultipleLocationProducts] = useState({});
  const [allMatchingProducts, setAllMatchingProducts] = useState([]);

  const { selectedBusiness, runWithGuard } = useAuth();
  const getInventories = async () => {
    setInventoryLoading(true);
    runWithGuard(async () => {
      const res = await callApi(
        `/protected/business/${selectedBusiness.id}/getproducts`
      );
      if (res && res.status === true) {
        setInventories(
          orderBy(
            sortBy(formatProductCategories(res.data.productcategories), "name"),
            (elem) => Number(elem.order)
          )
        );
        setInventoryLoading(false);
      } else {
        toast.error(res?.message || "Failed to fetch inventories!");
        setInventoryLoading(false);
      }
    });
  };

  const getInventoriesForAnotherBusiness = async (bid) => {
    const res = await callApi(`/protected/business/${bid}/getproducts`);
    return res;
  };

  const getVendors = async () => {
    setInventoryLoading(true);
    runWithGuard(async () => {
      const res = await callApi(
        `/protected/business/${selectedBusiness.id}/vendor/get`
      );
      if (res && res.status === true) {
        setVendors(orderBy(res?.data, "name"));
        setInventoryLoading(false);
      } else {
        toast.error(res?.message || "Failed to fetch vendors!");
        setInventoryLoading(false);
      }
    });
  };

  const getPOTableConfig = async () => {
    // setInventoryLoading(true);
    runWithGuard(async () => {
      const res = await callApi(
        `/protected/business/${selectedBusiness.id}/tablesettings/po-settings/get`
      );
      if (res && res.status === true) {
        setPoConfig(!res.data ? "inhand-x-fullquantity" : res.data);
        setInventoryLoading(false);
      } else {
        toast.error(res.message || "Failed to fetch purchase order config!");
        setInventoryLoading(false);
      }
    });
  };

  const getMatchingProducts = async (id) => {
    const res = await callApi(
      `/protected/business/${selectedBusiness.id}/matchingproducts/${id}`
    );
    if (res?.status) {
      setAllMatchingProducts(res?.data?.map(p => ({
        ...p,
        attributes: parseJSON(p.attributes, true) || {},
      })));
    }
    return res;
  };

  const stockReasons = [
    {
      id: 0,
      heading: "Choose",
      label: "Choose a Reason",
      value: "Choose Reason",
      subtract: false,
    },
    {
      id: 1,
      heading: "Add New",
      label: "New Stock Received",
      value: "NEWSTOCK",
      subtract: false,
      question: "How many new stocks were received?",
    },
    {
      id: null,
      heading: "Add Quantity",
      label: "Recount (Add)",
      value: "ADJUSTMENT-ADD",
      subtract: false,
    },
    {
      id: null,
      heading: "Reduce Quantity",
      label: "Recount (Subtract)",
      value: "ADJUSTMENT-SUB",
      subtract: true,
    },
    {
      id: 4,
      heading: "Add Damaged Items",
      label: "Items Damaged",
      value: "DAMAGED",
      subtract: true,
      question: "How many items were damaged?",
    },
    {
      id: 5,
      heading: "Add Lost Items",
      label: "Items Lost",
      value: "LOST",
      subtract: true,
      question: "How many items were lost?",
    },
    {
      id: 6,
      heading: "Add Stolen Items",
      label: "Items Stolen",
      value: "STOLEN",
      subtract: true,
      question: "How many items were stolen?",
    },
    {
      id: 7,
      heading: "Add Used Items",
      label: "Items Used",
      value: "USED",
      subtract: true,
      question: "How many items were used?",
    },
    {
      id: 2,
      heading: "Add Quantity",
      label: "Recount",
      value: "ADJUSTMENT",
      subtract: false,
      exact: true,
      question: "How many quantity of the product is left in stock?",
    },
    {
      id: 8,
      heading: "Reduce Quantity",
      label: "Other reasons",
      value: "OTHER",
      exact: true,
      subtract: true,
      question: "How many quantity of the product is left in stock?",
    },
  ];

  const firstUpdate = useRef(true);

  const businessProductUpdate = (data) => {
    if (!inventories) return getInventories();
    const allInventories = inventories?.map((x) => {
      if (x.id !== data.category_id) {
        return x;
      }
      return {
        ...x,
        products: x.products?.map((el) => {
          if (el.id === data.id) return { ...el, ...data };
          return el;
        }) || [data],
      };
    });
    setInventories(allInventories);
    return null;
  };

  const updateBarCodeCache = (pid, barcode, data) => {
    const sids = Array.isArray(pid) ? pid : [pid];

    if (!pid || !barcode) return;

    setInventories(
      inventories?.map((x) => ({
        ...x,
        products: x.products?.map((el) => {
          if (sids.includes(el.id))
            return {
              ...el,
              barcode,
              product_code: barcode,
            };
          return el;
        }),
      }))
    );

    const tempMatchingProducts =
      allMatchingProducts?.map((x) =>
        sids.includes(x.id)
          ? {
              ...x,
              barcode,
              product_code: barcode,
            }
          : x
      ) || [];

    if (
      !Array.isArray(pid) &&
      !tempMatchingProducts?.find((x) => x.id === pid) &&
      data
    ) {
      tempMatchingProducts.push(data);
    }

    setAllMatchingProducts(tempMatchingProducts);
  };

  const convertProductsTaxable = (id, taxable) => {
    const allInventories = inventories?.map((cat) => {
      if (cat.id !== id) return cat;
      return {
        ...cat,
        products: cat?.products?.map((x) => ({
          ...x,
          attributes: { ...x.attributes, taxable },
        })),
      };
    });
    setInventories(allInventories);
    return null;
  };

  useEffect(() => {
    if (firstUpdate.current) {
      firstUpdate.current = false;
      return;
    }
    setInventories(null);
    setMultipleLocationProducts({});
  }, [selectedBusiness?.id]);

  return {
    inventories,
    setInventories,
    setSelectedVendorItems,
    multipleLocationProducts,
    setMultipleLocationProducts,
    selectedVendorItem,
    getInventories,
    getInventoriesForAnotherBusiness,
    getMatchingProducts,
    getPOTableConfig,
    poConfig,
    vendors,
    inventoryLoading,
    isRefresh,
    setVendors,
    getVendors,
    setRefresh,
    stockReasons,
    businessProductUpdate,
    allMatchingProducts,
    setAllMatchingProducts,
    updateBarCodeCache,
    convertProductsTaxable,
  };
};
